import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ComplaintsRegisterResult } from '@whinerp/client';
import { formatDate } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/** The complaints register (G-203): the list, and a case's own complaint controls. */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function ComplaintsRegisterPanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const register = useQuery<ComplaintsRegisterResult, ApiError>({ queryKey: ['complaints'], queryFn: () => api.complaintsRegister({ query: {} }) });
  if (!register.data || register.data.complaints.length === 0) return null;
  return (
    <Panel data-region="complaints">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('complaints.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('complaints.policy', { values: { ack: String(register.data.policy.acknowledgeDays), respond: String(register.data.policy.respondDays) } })}</p>
        <ul className="flex flex-col gap-1 text-dense">
          {register.data.complaints.map((row) => (
            <li key={row.caseId} data-complaint={row.caseId} data-breached={row.acknowledgeBreached || row.respondBreached} className="flex flex-wrap gap-2">
              <a className="text-ink underline" href={`/cases/${row.caseId}`}>{row.number}</a>
              <span className="text-ink-600">{row.category}</span>
              <span className={row.acknowledgeBreached ? 'text-danger-text' : 'text-ink-meta'}>
                {row.acknowledgedAt ? t('complaints.acknowledged', { values: { date: formatDate(row.acknowledgedAt, format) } }) : t('complaints.ackDue', { values: { date: formatDate(row.acknowledgeDue, format) } })}
              </span>
              <span className={`ms-auto ${row.respondBreached ? 'text-danger-text' : 'text-ink-meta'}`}>
                {row.finalResponseAt ? t('complaints.responded', { values: { date: formatDate(row.finalResponseAt, format) } }) : t('complaints.respondDue', { values: { date: formatDate(row.respondDue, format) } })}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}

export function CaseComplaintPanel({ caseId }: { caseId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const register = useQuery<ComplaintsRegisterResult, ApiError>({ queryKey: ['complaints'], queryFn: () => api.complaintsRegister({ query: {} }) });
  const [category, setCategory] = useState('');
  const [response, setResponse] = useState('');
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['complaints'] });
    void queryClient.invalidateQueries({ queryKey: ['case', caseId] });
  };
  const mark = useMutation({ mutationFn: () => api.markComplaint({ path: { caseId }, body: { category } }), onSuccess: refresh });
  const respond = useMutation({ mutationFn: () => api.finalComplaintResponse({ path: { caseId }, body: { body: response } }), onSuccess: refresh });
  if (!register.data) return null;
  const row = register.data.complaints.find((entry) => entry.caseId === caseId);
  return (
    <Panel data-region="case-complaint">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('complaints.caseTitle')}</SectionLabel>
        {!row ? (
          <form className="flex flex-wrap items-end gap-2" data-complaint-form onSubmit={(event) => { event.preventDefault(); mark.mutate(); }}>
            <input className={`${INPUT} flex-1`} required maxLength={120} data-complaint-category placeholder={t('complaints.category')} value={category} onChange={(event) => setCategory(event.target.value)} />
            <Button size="small" type="submit" data-complaint-mark disabled={mark.isPending}>{t('complaints.mark')}</Button>
          </form>
        ) : (
          <>
            <p className="text-dense text-ink" data-complaint-status={row.finalResponseAt ? 'answered' : 'open'}>
              {row.category} · {row.finalResponseAt
                ? t('complaints.responded', { values: { date: formatDate(row.finalResponseAt, format) } })
                : t('complaints.respondDue', { values: { date: formatDate(row.respondDue, format) } })}
            </p>
            {!row.finalResponseAt ? (
              <form className="flex flex-col gap-2" data-final-form onSubmit={(event) => { event.preventDefault(); respond.mutate(); }}>
                <textarea className="min-h-20 rounded-control border border-control bg-surface p-2 text-body text-ink" required data-final-body placeholder={t('complaints.finalPlaceholder')}
                  value={response} onChange={(event) => setResponse(event.target.value)} />
                <div><Button size="small" intent="primary" type="submit" data-final-send disabled={respond.isPending}>{t('complaints.sendFinal')}</Button></div>
              </form>
            ) : null}
          </>
        )}
        {[mark.error, respond.error].map((error, index) => (error ? <p key={index} className="text-dense text-danger-text">{errorText(error as ApiError)}</p> : null))}
      </div>
    </Panel>
  );
}
